import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { sectors } from '../../mock';
import PageHeader from '../innovher/PageHeader';
import Magnetic from '../innovher/Magnetic';
import FloatingOrbs from '../innovher/FloatingOrbs';
import SEO from '../SEO';

export default function SectorPage() {
  const { slug } = useParams();
  const sector = sectors.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug]);

  if (!sector) return <Navigate to="/" replace />;
  const others = sectors.filter((s) => s.slug !== sector.slug);

  return (
    <>
      <SEO 
        title={`${sector?.title || 'Sector'} | InnovHer`}
        description={`Explore how InnovHer builds and accelerates ventures in ${sector?.title || 'this'} sector, from early shaping to scale.`}
        keywords={`${sector?.title || 'Sector'} startups Jaipur, InnovHer portfolio, venture builder Rajasthan`}
      />
      <PageHeader
        eyebrow="Focus area"
        title={<>{sector.title}<span className="block text-[#ff8ec5] text-3xl md:text-4xl mt-3 font-normal italic">{sector.tagline}</span></>}
        subtitle={sector.desc}
        image={sector.image}
        breadcrumbs={[{ label: 'Sectors' }, { label: sector.title }]}
      />

      <section className="relative bg-[#08080a] py-24 lg:py-28 overflow-hidden">
        <FloatingOrbs />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div className="reveal-up">
            <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">Overview</div>
            <h2 className="text-white font-semibold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
              Why we&rsquo;re bullish on {sector.title.toLowerCase()}.
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              India is at an inflection point. Rising disposable incomes, digital-native consumers and a maturing investor base have created a generational window for {sector.title.toLowerCase()} founders. Innovher backs builders who combine craft with commercial discipline.
            </p>
            <p className="mt-4 text-white/55 leading-relaxed">
              Our acceleration sprint pairs sector specialists with founders for weekly sessions, monthly reviews, and on-demand capital and partnership unlocks.
            </p>
            <Magnetic>
              <Link to="/apply" data-cursor="hover" className="mt-8 group inline-flex items-center gap-2 pl-6 pr-2 py-2 rounded-full bg-[#d42b92] text-white font-medium hover:bg-white hover:text-black transition">
                Apply for this sector
                <span className="w-8 h-8 rounded-full bg-white text-[#d42b92] group-hover:bg-[#d42b92] group-hover:text-white grid place-items-center group-hover:rotate-45 transition">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            </Magnetic>
          </div>

          <div className="reveal-up grid grid-cols-2 gap-3">
            <div className="col-span-2 rounded-3xl overflow-hidden border border-white/10">
              <img src={sector.image} alt={sector.title} className="w-full aspect-[16/10] object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="rounded-3xl overflow-hidden border border-white/10">
              <img src={sector.image2} alt="" className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="rounded-3xl bg-[#d42b92] text-white p-6 flex flex-col justify-between glow-pink">
              <div className="text-[11px] uppercase tracking-widest text-white/85">Sector lens</div>
              <div>
                <div className="text-4xl font-semibold leading-none">{sector.highlights.length}</div>
                <div className="text-white/85 text-sm mt-1">key bets we&rsquo;re placing</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#0a0a0c] text-white py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up max-w-2xl mb-12">
            <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">Our focus inside the sector</div>
            <h3 className="font-semibold tracking-tight text-3xl md:text-4xl lg:text-5xl leading-tight">
              Where craft meets <span className="text-[#ff8ec5]">commercial</span>.
            </h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sector.highlights.map((h, i) => (
              <div key={h} className="reveal-up p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#d42b92] transition" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="w-9 h-9 rounded-full bg-[#d42b92]/15 grid place-items-center mb-5">
                  <Check className="w-4 h-4 text-[#ff8ec5]" />
                </div>
                <div className="text-lg font-medium">{h}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#08080a] py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <h3 className="text-3xl md:text-4xl font-semibold tracking-tight text-white">Explore other sectors</h3>
            <Link to="/#sectors" className="text-sm text-white/60 hover:text-[#ff8ec5] inline-flex items-center gap-1">All sectors <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/sectors/${o.slug}`} className="group relative rounded-3xl overflow-hidden border border-white/10 hover:border-[#d42b92] transition" data-cursor="hover">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={o.image} alt={o.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between text-white">
                    <div>
                      <div className="text-xl font-semibold">{o.title}</div>
                      <div className="text-white/80 text-sm">{o.tagline}</div>
                    </div>
                    <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
